import VisuallyHidden from "@reach/visually-hidden";
import { Box, Flex } from "grid-styled";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { borderColor, borders } from "styled-system";
import system from "system-components";

import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import { withTenantContext } from "pstat-anywhere/context_providers/TenantContext";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import { userDisplay } from "pstat-anywhere/utils/userDisplay";
import Card from "pstat-design-system/Card";
import RouterFocusLink from "pstat-design-system/RouterFocusLink";
import SiteLogo from "pstat-design-system/SiteLogo";
import { Text } from "pstat-design-system/text/paragraph";
import Tooltip from "pstat-design-system/Tooltip";
import { RegulationItem }
  from "pstat-anywhere/components/document_control/view_policy/standards_and_regulations/StandardsAndRegulationsPanel";

const LeftBox = styled(Box)`
  text-align: right;
`;

export const PreviewValueSpan = system({
  is: Text,
  display: "inline-block",
  p: 1
});

export const AuthorSearchLink = props => {
  const author = props.author;
  const pathname = `${getRootUrl()}/search`;
  const query = {
    author: author.pk
  };
  return (
    <RouterFocusLink
      to={{
        pathname: pathname,
        query: query
      }}
    >
      {userDisplay(author)}
    </RouterFocusLink>
  );
};

export const CategorySearchLink = props => {
  const category = props.category;
  const pathname = `${getRootUrl()}/search`;
  const query = {
    category: category.pk
  };
  return (
    <RouterFocusLink
      to={{
        pathname: pathname,
        query: query
      }}
    >
      {category.name}
    </RouterFocusLink>
  );
};

const ApplicabilitySectionLink = props => {
  const applicability = props.applicability;
  const pathname = window.location.pathname;
  const hash = "#applicability-panel";
  return (
    <RouterFocusLink
      to={{
        pathname: pathname,
        hash: hash
      }}
      data-tip={true}
      data-for="applicability-tenants-tooltip"
    >
      {applicability.name}
    </RouterFocusLink>
  );
};

const StandardsAndRegulationsSectionLink = ({ moreCounter }) => {
  const { t } = useTranslation();
  const pathname = window.location.pathname;
  const hash = "#standards-and-regulations-panel";
  return (
    <RouterFocusLink
      pb={1}
      to={{
        pathname: pathname,
        hash: hash
      }}
    >
      {t("nav.primary.manage.standardsAndRegs.counter", {count: moreCounter})}
    </RouterFocusLink>
  );
};

const Tags = props => {
  return props.tags.map((tag, index) => {
    const pathname = `${getRootUrl()}/search`;
    const query = {
      reference: tag.pk
    };
    let tagText = tag.name;
    if (index !== props.tags.length - 1) {
      tagText += ",";
    }
    if (props.isPreview) {
      return <PreviewValueSpan>{tagText}</PreviewValueSpan>;
    } else {
      return (
        <RouterFocusLink
          key={tag.pk}
          to={{
            pathname: pathname,
            query: query
          }}
        >
          {tagText}
        </RouterFocusLink>
      );
    }
  });
};

const Regulations = ({ regulations, numRegulationsDisplay }) => {
  return regulations.map((regulation, index) => {
    if (index < numRegulationsDisplay) {
      return (
        <Flex>
          <RegulationItem {...regulation} newLine={true}/>
        </Flex>
      )
    } else if (index === numRegulationsDisplay) {
      return (
        <Flex>
          <StandardsAndRegulationsSectionLink
            moreCounter={regulations.length-numRegulationsDisplay}
          />
        </Flex>
      );
    }
  });
};

const KeyValueRow = props => {
  return (
    <Flex id={props.id} flexDirection="row" justify="center">
      <LeftBox width={1 / 2} py={1} px={2}>
        <Text color="nav.25">{props.label}</Text>
      </LeftBox>
      <Box width={1 / 2} px={2}>
        {props.children}
      </Box>
    </Flex>
  );
};

const DateKeyValueRow = ({ id, label, value }) => {
  const { t } = useTranslation();
  let displayValue = value;
  let screenReaderFullDate = value;
  if (value === null) {
    displayValue = "-";
    screenReaderFullDate = t(
      "documentControl.view.infoModal.dateDisplay.notApplicable"
    );
  }
  return (
    <Flex id={id} flexDirection="row" justify="center">
      <VisuallyHidden>{`${label}, ${screenReaderFullDate}`}</VisuallyHidden>
      <LeftBox width={1 / 2} py={1} px={2}>
        <Text color="nav.25" aria-hidden="true">
          {label}
        </Text>
      </LeftBox>
      <Box width={1 / 2} py={1} px={2}>
        <Text aria-hidden="true">{displayValue}</Text>
      </Box>
    </Flex>
  );
};

const Dates = ({
  labels,
  tenant,
  effectiveDate,
  approvalDate,
  expirationDate,
  revisionDate,
  originationDate
}) => {
  const { documentSettings } = tenant;
  return (
    <Fragment>
      {documentSettings.originationDateShow && (
        <DateKeyValueRow
          id="origination_date"
          label={labels.originationDateLabel}
          value={originationDate}
        />
      )}
      {documentSettings.approvedDateShow && (
        <DateKeyValueRow
          id="approved_date"
          label={labels.approvedDateLabel}
          value={approvalDate}
        />
      )}
      {tenant.hasScheduledEffectiveDateEnabled &&
        documentSettings.effectiveDateShow && (
          <DateKeyValueRow
            id="effective_date"
            label={labels.effectiveDateLabel}
            value={effectiveDate}
          />
        )}
      {documentSettings.revisionDateShow && (
        <DateKeyValueRow
          id="revision_date"
          label={labels.revisionDateLabel}
          value={revisionDate}
        />
      )}
      {documentSettings.expirationDateShow && (
        <DateKeyValueRow
          id="expiration_date"
          label={labels.expirationDateLabel}
          value={expirationDate}
        />
      )}
    </Fragment>
  );
};

const CenteredDiv = system({
  textAlign: "center"
});

export const ApplicabilityTenantsTootip = props => {
  const tenantList = props.bloc.tenants.map(tenant => (
    <CenteredDiv key={tenant.pk}>{tenant.name}</CenteredDiv>
  ));
  return <Tooltip name="applicability-tenants-tooltip">{tenantList}</Tooltip>;
};

const Ownership = ({
  labels,
  documentSettings,
  author,
  category,
  bloc,
  tenant,
  tags,
  isPreview,
  regulations,
  numRegulationsDisplay
}) => {
  const { t } = useTranslation();
  return (
    <Fragment>
      {documentSettings.authorShow && (
        <KeyValueRow id="author" label={labels.authorLabel}>
          {isPreview ? (
            <PreviewValueSpan>
              {author.firstName} {author.lastName}
            </PreviewValueSpan>
          ) : (
            <AuthorSearchLink author={author} />
          )}
        </KeyValueRow>
      )}
      {documentSettings.departmentShow && (
        <KeyValueRow id="category" label={labels.categoryLabel}>
          {isPreview ? (
            <PreviewValueSpan>{category.name}</PreviewValueSpan>
          ) : (
            <CategorySearchLink category={category} />
          )}
        </KeyValueRow>
      )}
      {tenant.showApplicability && (
        <KeyValueRow id="applicability" label="Applicability">
          {isPreview ? (
            <PreviewValueSpan>{bloc.name}</PreviewValueSpan>
          ) : (
            <ApplicabilitySectionLink applicability={bloc} />
          )}
        </KeyValueRow>
      )}
      {documentSettings.referencesShow && (
        <Fragment>
          {tags.length > 0 && (
            <KeyValueRow id="tags" label={labels.referencesLabel}>
              <Tags tags={tags} isPreview={isPreview} />
            </KeyValueRow>
          )}
        </Fragment>
      )}
      {regulations && (
        <Fragment>
          {regulations.length > 0 && (
            <KeyValueRow
              id="standards-and-regulations"
              label={t("nav.primary.manage.standardsAndRegs")}
            >
              <Regulations
                regulations={regulations}
                numRegulationsDisplay={numRegulationsDisplay}
              />
            </KeyValueRow>
          )}
        </Fragment>
      )}
      <ApplicabilityTenantsTootip bloc={bloc} />
    </Fragment>
  );
};

const BoxWithBorder = styled(Box)`
  ${borders};
  ${borderColor};
`;

const OwnershipBox = styled(Box).attrs({
  "data-testid": "policy-properties-ownership"
})`
  & a div {
    max-width: 120px;
    word-break: break-word;
  }
`;

const PolicyProperties = ({
  effectiveDate,
  approvalDate,
  expirationDate,
  revisionDate,
  originationDate,
  author,
  category,
  bloc,
  tags,
  labels,
  tenant,
  headerImageUrl,
  regulations,
  isPreview = false,
  numRegulationsDisplay
}) => (
  <Card id="document_header">
    <Flex>
      <Flex alignItems="center" justify="center" width={1 / 3} height="0%">
        <SiteLogo src={headerImageUrl} />
      </Flex>
      <BoxWithBorder width={1 / 3} borderRight="1px solid" borderColor="nav.80">
        <Dates
          labels={labels}
          tenant={tenant}
          effectiveDate={effectiveDate}
          approvalDate={approvalDate}
          expirationDate={expirationDate}
          revisionDate={revisionDate}
          originationDate={originationDate}
        />
      </BoxWithBorder>
      <OwnershipBox width={1 / 3}>
        <Ownership
          labels={labels}
          documentSettings={tenant.documentSettings}
          author={author}
          category={category}
          bloc={bloc}
          tags={tags}
          isPreview={isPreview}
          tenant={tenant}
          regulations={regulations}
          numRegulationsDisplay={numRegulationsDisplay}
        />
      </OwnershipBox>
    </Flex>
  </Card>
);

export default withTenantContext(withLabelContext(PolicyProperties));
